import { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useSteps } from '@features/Steps';
import { splitCamelCase } from '@shared/utils';
import classNames from 'classnames';
import { UserManagementBody } from '@shared/api/ServiceAPI/UserMangement';
import { Input, Select, SelectOption } from '@shared/UI';
import { BiEditAlt } from 'react-icons/bi';
import { HiOutlineInformationCircle } from 'react-icons/hi';
import { Tooltip } from 'react-tooltip';
import { getSelectValue } from '@widgets/AccountsTable/components/AccountsViewDrawer/utils/getSelectValue';

interface AccountsInfoProps {
    fieldName: string;
    value: string | number | null;
    className?: string;
    shouldSplit?: boolean;
    editableField?: 'select' | 'input';
    keyName?: string;
    options?: SelectOption[];
    onChange?: (key: string, value: string | boolean | number) => void;
}

const AccountsInfo: React.FC<AccountsInfoProps> = (props) => {
    const { fieldName, value, className, editableField, onChange, keyName, options } = props;

    const [openEdit, setOpenEdit] = useState(false);

    const renderEditableField = useMemo(() => {
        if (!openEdit || !keyName) {
            return value;
        }

        switch (editableField) {
            case 'input':
                return (
                    <Input
                        editable
                        value={value?.toString()}
                        onChange={(e) => onChange?.(keyName, e.target.value)}
                    />
                );
            case 'select':
                return (
                    <Select
                        currentValue={getSelectValue(fieldName, value ? value?.toString() : '').toString()}
                        onSelect={(value) => {
                            onChange?.(keyName!, value);
                        }}
                        options={options || []}
                    />
                );
            default:
                return value;
        }
    }, [openEdit, value]);

    return (
        <div className={classNames('widget-trade-status-table-drawer__info-item', className)}>
            <span
                data-tooltip-id={fieldName}
                className='widget-trade-status-table-drawer__info-header'
            >
                <HiOutlineInformationCircle id={fieldName} />
                <Tooltip
                    id={fieldName}
                    place={'bottom'}
                    content={`Some info about ${fieldName}`}
                    opacity={1}
                    style={{
                        backgroundColor: '#FFFFFF',
                        padding: 12,
                        borderRadius: 12,
                        fontSize: 12,
                        color: '#000000BF',
                        boxShadow: '0px 0px 6px -1px #0000001A',
                    }}
                />
                {fieldName}: {''}
            </span>
            <span
                className={classNames('widget-trade-status-table-drawer__info-content', { nullable: !value })}
                style={{ float: 'right' }}
            >
                {renderEditableField}
                {editableField && <BiEditAlt onClick={() => setOpenEdit((prev) => !prev)} />}
            </span>
        </div>
    );
};

export const GeneralTransactions: FC<{
    OrderBody: any;
    setEditedValues: Dispatch<SetStateAction<UserManagementBody>>;
    editedValues: any;
}> = ({ OrderBody, setEditedValues, editedValues }) => {
    const { pushAllowedStep } = useSteps();

    const userStorage = localStorage.getItem('User');
    const userObject = JSON.parse(userStorage ? userStorage : '');

    const onChange = (key: string, value: string | boolean | number) => {
        setEditedValues({ ...editedValues, [key]: value });
    };

    useEffect(() => {
        pushAllowedStep(0);
        pushAllowedStep(1);
    }, [pushAllowedStep]);

    return (
        <div className='widget-transaction-view-drawer__content'>
            <AccountsInfo
                className={classNames({ noBorder: OrderBody.firstName === 'processed' })}
                keyName={'updatedPrice'}
                fieldName={splitCamelCase('price', 'every-word')}
                editableField={userObject?.userRole !== 4 && editedValues.remainingQuantity !== 0 ? 'input' : undefined}
                onChange={onChange}
                value={editedValues.updatedPrice}
            />

            {userObject?.userRole !== 4 && (
                <AccountsInfo
                    value={editedValues.price}
                    fieldName={splitCamelCase('ActualPrice', 'every-word')}
                />
            )}

            <AccountsInfo
                value={editedValues.quantity}
                fieldName={splitCamelCase('quantity', 'every-word')}
            />

            <AccountsInfo
                value={editedValues.remainingQuantity === 0 ? 'Assigned' : 'Unassigned'}
                fieldName={splitCamelCase('Status', 'every-word')}
            />
        </div>
    );
};
